import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useStandardizedTracking,
  EventNames,
  ContentTypes,
  FeatureGroups,
  PageKeys,
  InlineEditContentKeys
} from "../../../utils/pendo/useStandardizedTracking";
import {
  selectFilterList,
  selectFilters,
  selectSelectedView,
  selectSorting,
  selectTableColumns
} from "../../../store/selector";
import { selectedViews } from "../../../constant/table";
import { EXCLUDED_EXPORT_COLUMNS } from "../../../constant/export";

const formatSortOrder = (sorting) => {
  if (!sorting || Object.keys(sorting).length === 0) return [];
  return Object.entries(sorting)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${value}`);
};

const getVisibleColumns = (tableColumns, selectedView) => {
  if (!tableColumns || !tableColumns[selectedView]) return [];
  return Object.keys(tableColumns[selectedView]).filter(
    (key) => tableColumns[selectedView][key]?.display && !EXCLUDED_EXPORT_COLUMNS.includes(key)
  );
};

const getPageKey = (selectedView, filterList) => {
  if (filterList?.id) return PageKeys.SAVED_LISTS;
  switch (selectedView) {
    case selectedViews.allPatients:
      return PageKeys.ALL_PATIENTS;
    case selectedViews.visitSchedule:
      return PageKeys.VISIT_SCHEDULE;
    case selectedViews.highPriority:
      return PageKeys.HIGH_PRIORITY;
    case selectedViews.savedList:
      return PageKeys.SAVED_LISTS;
    default:
      return PageKeys.ALL_PATIENTS;
  }
};

export const useInlineEditTracking = () => {
  const { track } = useStandardizedTracking();

  const filterList = useSelector(selectFilterList);
  const filters = useSelector(selectFilters);
  const selectedView = useSelector(selectSelectedView);
  const sorting = useSelector(selectSorting);
  const tableColumns = useSelector(selectTableColumns);

  const buildCommonAdditionalInfo = useCallback(
    (customInfo = {}) => {
      const isSavedList = !!filterList?.id;
      return {
        is_saved_list: isSavedList,
        saved_list_id: filterList?.id || null,
        saved_list_name: filterList?.name || null,
        saved_list_origin_bucket: filterList?.originBucket || null,
        total_count_available_in_list: filterList?.totalCount || null,
        sort_order: formatSortOrder(sorting),
        columns_visible: getVisibleColumns(tableColumns, selectedView),
        filters_applied: filters || {},
        ...customInfo
      };
    },
    [filterList, filters, sorting, tableColumns, selectedView]
  );

  const pageKey = useMemo(() => getPageKey(selectedView, filterList), [selectedView, filterList]);

  const trackInlineEditViewed = useCallback(
    ({ attribute }) => {
      const additionalInfo = buildCommonAdditionalInfo({ attribute });
      track(EventNames.INLINE_EDIT_VIEWED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.INLINE_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: InlineEditContentKeys.INLINE_EDIT_VIEWED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  const trackInlineEditTriggered = useCallback(
    ({ attribute }) => {
      const additionalInfo = buildCommonAdditionalInfo({ attribute });
      track(EventNames.INLINE_EDIT_TRIGGERED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.INLINE_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: InlineEditContentKeys.INLINE_EDIT_TRIGGERED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  const trackInlineEditCompleted = useCallback(
    ({ attribute, status, runtimeMs }) => {
      const additionalInfo = buildCommonAdditionalInfo({
        attribute,
        status,
        runtime_ms: runtimeMs
      });
      track(EventNames.INLINE_EDIT_COMPLETED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.INLINE_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: InlineEditContentKeys.INLINE_EDIT_COMPLETED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  return {
    trackInlineEditViewed,
    trackInlineEditTriggered,
    trackInlineEditCompleted
  };
};

export default useInlineEditTracking;
